import { Preferences } from "@capacitor/preferences";
import logger from "./logger";

const PUZZLE_HISTORY_KEY = "puzzly_history";
const MIGRATED_KEY_PREFIX = "puzzly_migrated_";

export async function loadPuzzleHistory() {
  try {
    const { value } = await Preferences.get({ key: PUZZLE_HISTORY_KEY });
    if (!value) return [];
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    logger.error("Failed to load puzzle history", { error: err.message });
    return [];
  }
}

export async function savePuzzleHistory(puzzles) {
  try {
    await Preferences.set({
      key: PUZZLE_HISTORY_KEY,
      value: JSON.stringify(puzzles),
    });
  } catch (err) {
    logger.error("Failed to save puzzle history", { error: err.message });
  }
}

/**
 * Wipe the local puzzle history key from Capacitor Preferences.
 *
 * Phase 1 / W4.T1 — used by the account-merge flow when we want to
 * forcibly drop the local cache (e.g. after sign-out + re-bootstrap so the
 * fresh anon doesn't inherit the previous user's history). Best-effort:
 * errors are logged but never thrown — losing the local cache is recoverable.
 *
 * @returns {Promise<void>}
 */
export async function clearPuzzleHistory() {
  try {
    await Preferences.remove({ key: PUZZLE_HISTORY_KEY });
  } catch (err) {
    logger.error("Failed to clear puzzle history", { error: err.message });
  }
}

/**
 * Stamp a per-uid flag indicating that the local history has been migrated
 * to Firestore for this user. Lets `migrateLocalHistoryToUser` short-circuit
 * on subsequent sign-ins (avoiding redundant writeBatch calls + duplicate
 * docs).
 *
 * Stored as `"1"` at key `puzzly_migrated_${uid}` per Capacitor Preferences
 * convention (string values only).
 *
 * @param {string} uid
 * @returns {Promise<void>}
 */
export async function markHistoryMigrated(uid) {
  try {
    await Preferences.set({
      key: `${MIGRATED_KEY_PREFIX}${uid}`,
      value: "1",
    });
  } catch (err) {
    logger.error("Failed to mark history migrated", {
      uid,
      error: err.message,
    });
  }
}

/**
 * Check the per-uid migration flag set by `markHistoryMigrated`.
 *
 * @param {string} uid
 * @returns {Promise<boolean>} true iff the flag is `"1"`.
 */
export async function isHistoryMigrated(uid) {
  try {
    const { value } = await Preferences.get({
      key: `${MIGRATED_KEY_PREFIX}${uid}`,
    });
    return value === "1";
  } catch (err) {
    logger.error("Failed to read migration flag", {
      uid,
      error: err.message,
    });
    return false;
  }
}

const COACHMARKS_SEEN_KEY = "puzzly_coachmarks_seen";

/**
 * Stamp that the creator coach-mark walkthrough has been shown once.
 * Best-effort: errors logged, never thrown.
 * @returns {Promise<void>}
 */
export async function markCoachmarksSeen() {
  try {
    await Preferences.set({ key: COACHMARKS_SEEN_KEY, value: "1" });
  } catch (err) {
    logger.error("Failed to mark coachmarks seen", { error: err.message });
  }
}

/**
 * Whether the creator coach-mark walkthrough has already been shown.
 * @returns {Promise<boolean>} true iff the flag is "1".
 */
export async function coachmarksSeen() {
  try {
    const { value } = await Preferences.get({ key: COACHMARKS_SEEN_KEY });
    return value === "1";
  } catch (err) {
    logger.error("Failed to read coachmarks flag", { error: err.message });
    return false;
  }
}

const INTRO_SEEN_KEY = "puzzly_intro_seen";

/**
 * #127: stamp that the three first-launch screens have been shown (or skipped).
 * Best-effort: errors logged, never thrown.
 * @returns {Promise<void>}
 */
export async function markIntroSeen() {
  try {
    await Preferences.set({ key: INTRO_SEEN_KEY, value: "1" });
  } catch (err) {
    logger.error("Failed to mark intro seen", { error: err.message });
  }
}

/**
 * Whether the first-launch screens have already been shown. On a read error
 * it answers true: better to skip the intro than to show it on every launch.
 * @returns {Promise<boolean>}
 */
export async function introSeen() {
  try {
    const { value } = await Preferences.get({ key: INTRO_SEEN_KEY });
    return value === "1";
  } catch (err) {
    logger.error("Failed to read intro flag", { error: err.message });
    return true;
  }
}
